import {FormattedMessage, injectIntl} from "react-intl";
import {nfmsPost} from "../../handler/NfmsClient";
import React, {useEffect, useState} from "react";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Select, {SelectOption} from "@amzn/meridian/select";
import Row from "@amzn/meridian/row";
import Input from "@amzn/meridian/input";
import {ALERT_TYPES, AWS_APP_CONFIG_FIELD_TYPES, GRANULARITY_LEVELS, CUSTOM_VALUE_TYPES} from "../../Constants";
import {
    requiresCustomValue,
    validateCustomValueForSubmit,
    normalizeCustomValue,
    computeJsonMinifyOutcome,
    applyCustomValueOutcome,
    applyTypeChange,
    INVALID_JSON_MESSAGE
} from "./customValueTemplate";
import Button from "@amzn/meridian/button";
import Tag from "@amzn/meridian/tag";
import {getErrorAlertObjectFromResponse} from "../../Utils";
import Alert from "@amzn/meridian/alert";
import {fetchTemplates} from "../../NetworkUtils";
import Breadcrumb, {BreadcrumbGroup} from "@amzn/meridian/breadcrumb";
import {CREATE_FEATURES_BASE_ROUTE} from "../util/RouteConstants";

const AWSAppConfiguration = (props) => {

    const [awsAppConfigTemplate, setAwsAppConfigTemplate] = useState();
    const [accountID, setAccountID] = useState();
    const [applicationName, setApplicationName] = useState();
    const [environmentName, setEnvironmentName] = useState();
    const [configurationProfile,setConfigurationProfile] = useState();
    const [awsRegion, setAwsRegion] = useState();
    const [fieldName, setFieldName] = useState();
    const [fieldType, setFieldType] = useState();
    const [showMcmButton, setShowMcmButton] = useState(false);
    const [fields, setFields] = useState([]);
    const [historyProps, setHistoryProps] = useState([{featureId: "", configId: "", mcmId: ""}]);
    const [awsAppConfigTemplates, setAWSAppConfigTemplates] = useState([]);
    const [alert, setAlert] = useState({visible: false, text:"", type:""})
    const [granularityLevel, setGranularityLevel] = useState(GRANULARITY_LEVELS.STATION);
    // Per-config custom value + its type, used only by the ADD_NODE_WITH_CUSTOM_VALUE template.
    // Entered here at config-creation time (not at enable time) and persisted per-config so that at
    // enable time the backend writes {nodeId: customValue} for this specific config.
    const [customValue, setCustomValue] = useState();
    const [customValueType, setCustomValueType] = useState();
    const warningAlert = {visible: true, text: "Create AWS App Config request received.", type:ALERT_TYPES.WARNING};
    const successAlert = {visible: true, text: "Successfully created AWS App Config", type:ALERT_TYPES.SUCCESS};

    useEffect(() => {
        try {
            setHistoryProps({
                featureId: props.location.state.featureId,
                configId: props.location.state.configId,
                mcmId: props.location.state.mcmId
            });
            setAwsAppConfigTemplate(props.location.state.template);
            setAccountID(props.location.state.accountID);
            setApplicationName(props.location.state.applicationName);
            setEnvironmentName(props.location.state.environmentName);
            setConfigurationProfile(props.location.state.configurationProfile)
            setAwsRegion(props.location.state.awsRegion)
            setFields(props.location.state.fields ? props.location.state.fields : []);
            setGranularityLevel(props.location.state.granularityLevel || GRANULARITY_LEVELS.STATION);
            setCustomValue(props.location.state.customValue);
            setCustomValueType(props.location.state.customValueType);
        } catch(error) {}

        fetchTemplates("AWS_APP_CONFIG").then(response => setAWSAppConfigTemplates(response));
    }, []);

    useEffect(() => {
        if (!historyProps || !historyProps.mcmId) {
            setShowMcmButton(true);
        }
    }, [historyProps]);

    const removeField = (fieldToBeRemoved) => {
        const fieldsCopy = fields.filter(field => {return field !== fieldToBeRemoved;})
        setFields(fieldsCopy);
    }

    /*
        Clears a previously shown invalid-JSON alert (leaves any other alert untouched).
    */
    const clearInvalidJsonAlert = () => {
        setAlert(prev => prev.text === INVALID_JSON_MESSAGE ? {visible: false, text: "", type: ""} : prev);
    }


    const showInvalidJsonAlert = () => {
        setAlert({visible: true, type: ALERT_TYPES.ERROR, text: INVALID_JSON_MESSAGE});
    }

    const customValueStateCallbacks = {
        setType: setCustomValueType,
        setValue: setCustomValue,
        clearInvalidJsonAlert,
        showInvalidJsonAlert
    };

    /*
        Apply JSON minify/validation semantics to the current custom value. Valid JSON: minify in place
        and clear any prior invalid-JSON alert. Invalid JSON: surface an error alert immediately.
    */
    const minifyCustomValueIfJson = () => {
        applyCustomValueOutcome(computeJsonMinifyOutcome(customValue), customValueStateCallbacks);
    }

    /*
        Blur of the value field: validate/minify only when the selected type is already JSON. Handles
        the case where the user picks JSON first, then edits/pastes the value and clicks out. JSON is
        also normalized at submit time, so the stored value is always minified regardless.
    */
    const minifyCustomValueOnBlur = () => {
        if (customValueType !== CUSTOM_VALUE_TYPES.JSON) {
            return;
        }
        minifyCustomValueIfJson();
    }

    /*
        Type selection. On choosing JSON, minify whatever value is already entered (handles the flow
        where the user pastes the value first, then selects the type). On choosing STRING/LIST, clear
        any stale invalid-JSON alert — it no longer applies, since the value is now unconstrained for
        that type (otherwise the misleading error would linger and submit would still proceed). The
        whole behavior lives in applyTypeChange (unit-tested), so this is a one-line delegate.
    */
    const onCustomValueTypeChange = (selectedType) =>
        applyTypeChange(customValue, selectedType, customValueStateCallbacks);

    const addField = () => {
        const newField = {name: fieldName, type: fieldType};
        const fieldsCopy = Array.from(fields);
        fieldsCopy.push(newField);
        setFields(fieldsCopy);
    }

    const isCustomValueTemplate = requiresCustomValue(awsAppConfigTemplate);

    const createConfig = () => {
        // Custom-value template validation (required value + type, JSON parseable) lives behind the
        // customValueTemplate extension point rather than being inlined here.
        if (isCustomValueTemplate) {
            const validationError = validateCustomValueForSubmit(customValue, customValueType);
            if (validationError) {
                setAlert({visible: true, type: ALERT_TYPES.ERROR, text: validationError});
                return;
            }
        }
        setAlert(warningAlert);
        // Normalize (minify JSON) for storage; STRING/LIST returned as-is. No-op for non-custom templates.
        const normalizedCustomValue = isCustomValueTemplate
                ? normalizeCustomValue(customValue, customValueType)
                : undefined;
        const params = {
            AWSAppConfiguration: {
                featureId: historyProps.featureId,
                configurationId: historyProps.configId,
                accountID: accountID,
                template: awsAppConfigTemplate,
                applicationName: applicationName,
                environmentName: environmentName,
                configurationProfile: configurationProfile,
                awsRegion: awsRegion,
                mcmId: historyProps.mcmId,
                fields: fields,
                granularityLevel: granularityLevel,
                customValue: normalizedCustomValue,
                customValueType: isCustomValueTemplate ? customValueType : undefined,
            }
        };
        (async () => {
            nfmsPost("CreateAWSAppConfiguration", params)
                .then((response) => {
                    if (response.status === 200) {
                        setAlert(successAlert);
                    }
                })
                .catch((response) => {
                    setAlert(getErrorAlertObjectFromResponse(response));
                })
        })();
    }

    return (
        <div className="App" data-testid="app">
            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <BreadcrumbGroup>
                        <Breadcrumb href={`#${CREATE_FEATURES_BASE_ROUTE}`}>
                            <FormattedMessage id="createConfig.heading" defaultMessage="Create New Configuration"/>
                        </Breadcrumb>
                        <Breadcrumb href={"#"}>
                            <FormattedMessage id="awsAppConfig.heading" defaultMessage="Create New AWS App Configuration"/>
                        </Breadcrumb>
                    </BreadcrumbGroup>
                </Column>
            </Row>
            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="medium none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.template" defaultMessage="Template"/>
                    </Text>
                    <Select value={awsAppConfigTemplate} onChange={setAwsAppConfigTemplate}
                            placeholder="Select Template" width={400}>
                        {awsAppConfigTemplates.map(template => (
                            <SelectOption label={template} value={template} key={template} />
                        ))}
                    </Select>
                </Column>
            </Row>

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.accountID" defaultMessage="AWS App Config AccountID"/>
                    </Text>
                    <Input id="accountID" value={accountID} width={400}
                           onChange={setAccountID} type="text" placeholder="Enter AWS App Config Account ID"/>
                </Column>
            </Row>

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.applicationName" defaultMessage="Application Name"/>
                    </Text>
                    <Input id="applicationName" value={applicationName} width={400}
                           onChange={setApplicationName} type="text" placeholder="Enter Application name" />
                </Column>
            </Row>

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.environmentName" defaultMessage="Environment Name"/>
                    </Text>
                    <Input id="environmentName" value={environmentName} width={400}
                           onChange={setEnvironmentName} type="text" placeholder="Enter Environment Name"/>
                </Column>
            </Row>

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.configurationProfile" defaultMessage="Configuration Profile"/>
                    </Text>
                    <Input id="configurationProfile" value={configurationProfile} width={400}
                           onChange={setConfigurationProfile} type="text" placeholder="Enter Configuration Profile"/>
                </Column>
            </Row>

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.awsRegion" defaultMessage="AWS Region"/>
                    </Text>
                    <Input id="awsRegion" value={awsRegion} width={400}
                           onChange={setAwsRegion} type="text" placeholder="Enter AWS Region (Eg. us-east-1)"/>
                </Column>
            </Row>

            {showMcmButton ?
                <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="awsAppConfig.mcm" defaultMessage="MCM Id"/>
                        </Text>
                        <Input id="mcm-input" value={historyProps.mcmId}
                               onChange={id => setHistoryProps({...historyProps, mcmId: id})}
                               type="text" placeholder="Enter MCM Id" width={400}/>
                    </Column>
                </Row>
                : null}

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="none none medium none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.granularityLevel" defaultMessage="Granularity Level"/>
                    </Text>
                    <Select value={granularityLevel} onChange={setGranularityLevel}
                            placeholder="Select Granularity Level" width={400}>
                        <SelectOption label="STATION" value={GRANULARITY_LEVELS.STATION} key="STATION" />
                        <SelectOption label="CYCLE" value={GRANULARITY_LEVELS.CYCLE} key="CYCLE" />
                    </Select>
                </Column>
            </Row>

            <Row alignmentVertical="top" spacingInset="none large small none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.fieldName" defaultMessage="Field Name"/>
                    </Text>
                    <Input id="field-name" value={fieldName} width={150}
                           onChange={setFieldName} type="text" placeholder="Field Name"/>
                </Column>
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.fieldType" defaultMessage="Field Type"/>
                    </Text>
                    <Select  value={fieldType} onChange={setFieldType} placeholder="Field Type" width={130} >
                        {Object.entries(AWS_APP_CONFIG_FIELD_TYPES).map(([key, value]) => (
                            <SelectOption label={key} value={value} key={key} />
                        ))}
                    </Select>
                </Column>
                <Column spacing="200" alignmentHorizontal="left">
                    <Text type="b200" color="secondary">
                        <FormattedMessage id="awsAppConfig.addButton" defaultMessage="."/>
                    </Text>
                    <Button type="secondary" onClick={() => addField()} data-testid="add-button">
                        <FormattedMessage id="awsAppConfig.addButton" defaultMessage="Add Field"/>
                    </Button>
                </Column>
            </Row>

            <Row alignmentVertical="top" spacingInset="small small small none" wrap="down">
                {fields.map(field => (
                    <Tag type="neutral" key={field.name} onClose={() => removeField(field)} >
                        {field.name + " (" + field.type + ")"}
                    </Tag>
                ))}
            </Row>

            {isCustomValueTemplate ?
                <Row alignmentVertical="top" spacingInset="none none medium none">
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="awsAppConfig.customValue" defaultMessage="Custom Value"/>
                        </Text>
                        <Input id="custom-value" value={customValue} width={270}
                               onChange={setCustomValue} onBlur={minifyCustomValueOnBlur} type="text"
                               placeholder='Value stored as {NODE_ID: value}.'/>
                    </Column>
                    <Column spacing="200" alignmentHorizontal="left">
                        <Text type="b200" color="secondary">
                            <FormattedMessage id="awsAppConfig.customValueType" defaultMessage="Custom Value Type"/>
                        </Text>
                        <Select value={customValueType} onChange={onCustomValueTypeChange}
                                placeholder="Select Value Type" width={130}>
                            {Object.entries(CUSTOM_VALUE_TYPES).map(([key, value]) => (
                                <SelectOption label={key} value={value} key={key} />
                            ))}
                        </Select>
                    </Column>
                </Row>
                : null}

            <Row widths={["grid-3", "grid-2"]} alignmentVertical="top" spacingInset="large large large none">
                <Column spacing="200" alignmentHorizontal="left">
                    <Button onClick={createConfig} data-testid="create-config">
                        <FormattedMessage id="awsAppConfig.create" defaultMessage="Create Config"/>
                    </Button>
                </Column>
            </Row>
            <Row alignmentVertical="top" spacingInset="none none medium none">
                {alert.visible ? <Alert type={alert.type}>{alert.text}</Alert> : null}
            </Row>
        </div>
    );
}

export default injectIntl(AWSAppConfiguration);


// WEBPACK FOOTER //
// ./src/components/create-config/AWSAppConfiguration.js